import { Controller } from 'react-hook-form';
import {
  Typography,
  TextField as MuiTextField,
  Button as MuiButton,
  FormControl,
  Autocomplete,
  Collapse,
  IconButton,
  Grow,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { formatPhoneNumber } from 'utility/helpers';
import { LOGIN } from 'constants/routes';
import { SyntheticEvent, useRef } from 'react';
import DomainNotRegisteredModal from '../../login/components/DomainNotRegisteredModal';
import OrDivider from '../../login/components/OrDivider';
import { Link as RouterLink } from 'react-router-dom';
import { theme } from 'config/theme';
import ArrowLeftLineIcon from 'remixicon-react/ArrowLeftLineIcon';
import { RegistrationFormData, useSignupForm } from '../hooks/useSignupForm';
import { PasswordInput, RenderAuthOptions } from '@buildwise/libs/login';

export const titleOptions = [
  'Project Engineer (PE)',
  'Superintendent',
  'Assistant Project Manager (APM)',
  'Project Controls Manager',
  'Project Manager (PM)',
  'Senior PM',
  'Estimator',
  'Precon Manager',
  'Project Executive',
  'Ops Manager',
  'Intern',
  'Technology Manager',
  'Other',
];

export const expOptions = [
  'Less than 3 years',
  '3-8 years',
  '8-15 years',
  '15-25 years',
  'More than 25 years',
];

type SignupFormProps = {
  onSignup: (values: RegistrationFormData) => Promise<void>;
};

const SignupForm = ({ onSignup }: SignupFormProps) => {
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const {
    firstNameInputRef,
    showUnknownDomainModal,
    signupFormData,
    verifyDomainResponse,
    onSubmit,
    setShowUnknownDomainModal,
    setVerifyDomainResponse,
  } = useSignupForm({
    onSignup,
  });
  const {
    control,
    formState: { isSubmitting },
    getValues,
    handleSubmit,
    setValue,
  } = signupFormData;

  return (
    <Grow in style={{ transformOrigin: 'top center' }}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {verifyDomainResponse && (
          <IconButton
            sx={{ ml: -1, my: 2 }}
            title="Back"
            onClick={() => {
              setVerifyDomainResponse(undefined);
              setTimeout(() => {
                if (emailInputRef.current) {
                  emailInputRef.current.focus();
                }
              }, 0);
            }}
          >
            <ArrowLeftLineIcon size={25} color={theme.palette.custom.black} />
          </IconButton>
        )}
        <SignupTitle variant="h3">Sign Up</SignupTitle>
        <SignupDescription>Welcome to BuildWise!</SignupDescription>

        <Collapse in={!!verifyDomainResponse}>
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <TextField
                  {...field}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState?.error?.message || ''}
                  variant="outlined"
                  placeholder="First Name"
                  inputRef={firstNameInputRef}
                  autoFocus
                />
              </FormControl>
            )}
            name="firstname"
          />

          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState?.error?.message || ''}
                  {...field}
                  variant="outlined"
                  placeholder="Last Name"
                />
              </FormControl>
            )}
            name="lastname"
          />
        </Collapse>

        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <TextField
                autoFocus
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState?.error?.message || ''}
                {...field}
                variant="outlined"
                placeholder="Email"
                disabled={!!verifyDomainResponse}
                inputRef={emailInputRef}
              />
            </FormControl>
          )}
          name="email"
        />

        <Collapse in={!!verifyDomainResponse}>
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  helperText={fieldState?.error?.message || ''}
                  {...field}
                  onChange={(e) => {
                    field.onChange(formatPhoneNumber(e.target.value));
                  }}
                  variant="outlined"
                  placeholder="Phone Number (optional)"
                />
              </FormControl>
            )}
            name="phoneNumber"
          />

          <Controller
            control={control}
            render={({ field: { ref, ...field }, fieldState }) => (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Autocomplete
                  {...field}
                  freeSolo
                  autoHighlight
                  value={field.value || null}
                  onChange={(_: SyntheticEvent<Element>, value: string | null) =>
                    setValue('title', value || '', { shouldDirty: true, shouldValidate: true })
                  }
                  onInputChange={(_: SyntheticEvent<Element>, value: string) =>
                    setValue('title', value || '', { shouldDirty: true, shouldValidate: true })
                  }
                  options={titleOptions}
                  noOptionsText="No Job Titles Found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!fieldState.error}
                      helperText={fieldState?.error?.message || ''}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      placeholder="Job Title"
                    />
                  )}
                />
              </FormControl>
            )}
            name="title"
          />

          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState?.error?.message || ''}
                  {...field}
                  variant="outlined"
                  placeholder="Company Name"
                  disabled={!!verifyDomainResponse?.companyName}
                />
              </FormControl>
            )}
            name="primaryCompanyName"
          />

          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <PasswordInput
                  {...field}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={
                    fieldState?.error?.message ? (
                      <>
                        {fieldState?.error?.message.split('\n').map((m) => (
                          <span style={{ display: 'block' }} key={m}>
                            {m}
                          </span>
                        ))}
                      </>
                    ) : (
                      ''
                    )
                  }
                  variant="outlined"
                  placeholder="Password"
                  autoComplete="new-password"
                />
              </FormControl>
            )}
            name="password"
          />

          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <PasswordInput
                  {...field}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState?.error?.message || ''}
                  variant="outlined"
                  placeholder="Re-type Password"
                  autoComplete="new-password"
                />
              </FormControl>
            )}
            name="confirmPassword"
          />

          <TermsAndPrivacy>
            By continuing you indicate that you agree to BuildWise's Terms of Service and Privacy
            Policy
          </TermsAndPrivacy>
        </Collapse>
        <Button fullWidth variant="contained" disabled={isSubmitting} type="submit">
          {isSubmitting && verifyDomainResponse
            ? 'Signing Up...'
            : isSubmitting
              ? 'Verifying...'
              : verifyDomainResponse
                ? 'Sign Up'
                : 'Next'}
        </Button>
        <AlreadyHaveAccount>
          Already have an account? <Link to={LOGIN}>Log In</Link>
        </AlreadyHaveAccount>

        <OrDivider />

        <RenderAuthOptions verifyDomainResponse={verifyDomainResponse} />

        {showUnknownDomainModal && (
          <DomainNotRegisteredModal
            email={getValues().email}
            open={showUnknownDomainModal}
            setOpen={setShowUnknownDomainModal}
          />
        )}
      </Form>
    </Grow>
  );
};

const Form = styled('form', { label: 'Form' })(({ theme }) => ({
  maxWidth: 500,
  width: '100%',
  padding: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.custom.black,
      },
    },
  },
}));

const SignupTitle = styled(Typography, { label: 'SignupTitle' })(({ theme }) => ({
  fontSize: 36,
  fontWeight: 700,
  color: theme.palette.custom.mirage,
  [theme.breakpoints.down('md')]: {
    marginBottom: 32,
    fontSize: 24,
  },
}));

const SignupDescription = styled(Typography, { label: 'SignupDescription' })(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.custom.riverBed,
  margin: '12px 0 40px',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const TermsAndPrivacy = styled(Typography, { label: 'TermsAndPrivacy' })(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.custom.label,
  fontSize: 14,
  margin: '8px 0 16px',
}));

const Button = styled(MuiButton, { label: 'Button' })({
  borderRadius: 8,
  height: 56,
});

const AlreadyHaveAccount = styled(Typography, { label: 'AlreadyHaveAccount' })(({ theme }) => ({
  marginTop: 8,
  textAlign: 'center',
  color: theme.palette.custom.label,
}));

const Link = styled(RouterLink, { label: 'Link' })(({ theme }) => ({
  color: theme.palette.custom.black,
  fontWeight: 600,
  '&:hover, &:focus': {
    textDecoration: 'underline',
  },
}));

const TextField = styled(MuiTextField, { label: 'TextField' })(({ theme }) => ({
  '& .MuiOutlinedInput-root.Mui-disabled': {
    backgroundColor: theme.palette.custom.inputBackground,
  },
}));

export default SignupForm;
